import React from 'react';

import { useIntl } from 'react-intl-next';

import type { TriggerProps } from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { type ProductConfig, type ProductKeys } from '../../../../../common/constants/products';
import { PRODUCTS_WITH_INLINE_FILTERS } from '../../../../../common/constants/quick-find';
import { type NavigateCallbackEvent, useAppContext } from '../../../../../common/ui/app-context';
import { OAuthWarningWrapper } from '../../../../../common/ui/oauth-warning-wrapper';
import { FilterButton } from '../../../../../common/ui/quick-find/filter-button';
import { OAuthSource } from '../../../../../common/utils/events/oauth-events';
import { serveQFDropdownExperiment } from '../../../../../common/utils/experiments';
import { isProductSmartlink } from '../../../../../common/utils/oauth-container';
import { onAppFilterSelect as onAppFilterSelectEvent } from '../../../../../common/utils/quick-find/events/app-filter-selected';
import { ErrorBoundary } from '../../../../../controllers/error-boundary';
import { useIsUserOAuthed, useOAuthHandlers } from '../../../../../controllers/oauth';
import {
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../../../../../controllers/quick-find/analytics';
import {
	useAnyFilterSelected,
	useBootstrap,
	useSearchActions,
} from '../../../../../controllers/store';
import {
	useQuickFindActions,
	useQuickFindQuery,
} from '../../../../../controllers/store/quick-find';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { preloadSearchPageQuery } from '../../../../search-page-relay/utils/query-loader';

import { i18n } from './messages';
import { useInlineAppSelector, useInlineAppSelectorWithFilterDiscoverability } from './utils';

const inlineFilterStyles = xcss({
	display: 'flex',
	gap: 'space.100',
});

const noProductFilterStyles = xcss({
	marginRight: 'auto',
});

const AppButtonFor3PApps = ({
	productKey,
	redirectToFPSWithProductSelected,
	trigger,
}: {
	productKey: ProductKeys;
	productConfig: ProductConfig;
	redirectToFPSWithProductSelected: () => void;
	trigger: (props: { triggerProps?: TriggerProps; userNeedsAuth: boolean }) => JSX.Element;
}) => {
	const [{ thirdPartyConfigs }] = useBootstrap();
	const isSmartlink = isProductSmartlink({ productKey, thirdPartyConfigs });
	const { userNeedsOAuth } = useIsUserOAuthed(productKey);
	const quickFindQuery = useQuickFindQuery();

	if (userNeedsOAuth) {
		return (
			<ErrorBoundary section="QuickFindAppButtonFor3PAppsOAuthWrapper">
				<OAuthWarningWrapper
					key={productKey}
					product={productKey}
					postAuthAction={() => {
						redirectToFPSWithProductSelected();
					}}
					trigger={(triggerProps) => trigger({ ...triggerProps, userNeedsAuth: true })}
					isOnSearchDialog={true}
					useOAuthHandlers={useOAuthHandlers}
					useIsUserOAuthed={useIsUserOAuthed}
					isSmartlink={isSmartlink}
					searchQuery={quickFindQuery}
					oAuthSource={OAuthSource.quickFindFilter}
					thirdPartyConfigs={thirdPartyConfigs}
				/>
			</ErrorBoundary>
		);
	}

	return trigger({ userNeedsAuth: false });
};

type InlineAppSelectorProps = {
	onAllFiltersClickCallback?: (params: {
		event?: NavigateCallbackEvent;
		action?: 'filterByPrimaryProduct' | 'searchByAllApps' | 'none';
	}) => void;
};

// Refer to the folowing designs for the Quick Find Dialog responsiveness
// https://www.figma.com/design/50bR8aLZ4eCuXlkrejuCiw/Q3-FY25-Enterprise-Search-Design?node-id=1576-34214&t=GOmwvwnkhe6CCgJs-0
export const InlineAppSelector = ({
	onAllFiltersClickCallback,
}: InlineAppSelectorProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { onNavigate, generateSearchUrl, queryParams } = useAppContext();
	const { setQuickFindOpen } = useQuickFindActions();
	const quickFindQuery = useQuickFindQuery();
	const { selectProductFilter, clearAllFilters } = useSearchActions();
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();
	const {
		apps1PToDisplay,
		isDialogAbove400px,
		isDialogAbove450px,
		primaryProduct,
		selectedProduct,
	} = useInlineAppSelector();
	const [isAnyFilterSelected] = useAnyFilterSelected();

	const { apps3PToDisplay, numberOfAdditionalProducts } =
		useInlineAppSelectorWithFilterDiscoverability();

	const onAppFilterSelected = (
		productKey: ProductKeys,
		options: { redirectToFPS: boolean; isPostAuthAction: boolean },
		event?: NavigateCallbackEvent,
	) => {
		const newSelectedProduct = selectedProduct === productKey ? undefined : productKey;

		fireAnalyticsEvent(
			onAppFilterSelectEvent({
				actionSubjectId: productKey,
				action: newSelectedProduct === undefined ? 'unselected' : 'selected',
				attributes: {
					...commonAttributes,
					isPostAuthAction: options.isPostAuthAction,
					isRedirectedToFPS: options.redirectToFPS,
				},
				nonPrivacySafeAttributes,
			}),
		);

		if (!options.redirectToFPS) {
			selectProductFilter(newSelectedProduct);
			return;
		}

		// Preload FPS with the selected product and query
		preloadSearchPageQuery({
			search: { selectedProducts: [productKey], searchQuery: quickFindQuery },
		});

		const onNavigateCallback = typeof onNavigate === 'function' ? onNavigate : onNavigate.callback;

		onNavigateCallback(
			generateSearchUrl({
				...queryParams,
				product: productKey,
				text: quickFindQuery,
			}),
			'push',
			event,
		);

		clearAllFilters();

		setQuickFindOpen(false);
	};

	const apps3pComponent = apps3PToDisplay.map(({ productKey, productConfig }) => {
		const Icon = productConfig.IconBorderless ?? productConfig.Icon;

		return (
			<AppButtonFor3PApps
				key={productKey}
				productKey={productKey}
				productConfig={productConfig}
				redirectToFPSWithProductSelected={() =>
					onAppFilterSelected(productKey, { redirectToFPS: true, isPostAuthAction: true })
				}
				trigger={({ triggerProps, userNeedsAuth }) => (
					<FilterButton
						{...triggerProps}
						contentFit={isDialogAbove450px ? 'default' : 'compact'}
						iconBefore={<Icon size={'xsmall'} />}
						isSelected={false}
						onClick={(event) => {
							if (!userNeedsAuth) {
								onAppFilterSelected(
									productKey,
									{
										redirectToFPS: true,
										isPostAuthAction: false,
									},
									event,
								);
							}
						}}
						spacing={!userNeedsAuth || isDialogAbove400px ? 'default' : 'compact'}
						tooltip={
							!userNeedsAuth &&
							formatMessage(i18n.appTooltipFor1Pand3PApps, {
								productName: productConfig.displayName,
							})
						}
					>
						{isDialogAbove400px && productConfig.displayName}
					</FilterButton>
				)}
			/>
		);
	});

	const apps1PComponent = apps1PToDisplay.map(({ productKey, productConfig, redirectToFPS }) => {
		const AppIcon = productConfig.Icon;

		return (
			<FilterButton
				key={productKey}
				aria-current={selectedProduct === productKey}
				contentFit={isDialogAbove450px ? 'default' : 'compact'}
				iconBefore={<AppIcon size="small" />}
				isSelected={selectedProduct === productKey}
				onClick={(event) =>
					onAppFilterSelected(
						productKey,
						{
							redirectToFPS,
							isPostAuthAction: false,
						},
						event,
					)
				}
				tooltip={formatMessage(i18n.appTooltipFor1Pand3PApps, {
					productName: productConfig.displayName,
				})}
				spacing={isDialogAbove400px ? 'default' : 'compact'}
			>
				{isDialogAbove400px && productConfig.displayName}
			</FilterButton>
		);
	});

	if (!PRODUCTS_WITH_INLINE_FILTERS.includes(primaryProduct)) {
		return (
			<Box testId="qf-inline-app-selector" xcss={[inlineFilterStyles, noProductFilterStyles]}>
				{apps3pComponent}
				{apps1PComponent}
			</Box>
		);
	}

	if (
		isAnyFilterSelected &&
		serveQFDropdownExperiment(primaryProduct, { fireExperimentExposure: false })
	) {
		return null;
	}

	return (
		<Box testId="qf-inline-app-selector" xcss={inlineFilterStyles}>
			{apps3pComponent}
			{apps3PToDisplay.length > 0 && numberOfAdditionalProducts > 0 && (
				<FilterButton
					onClick={(event) => onAllFiltersClickCallback?.({ event, action: 'searchByAllApps' })}
					tooltip={formatMessage(i18n.tooltipForAllApps)}
					testId="quick-find-app-filters-all-apps-button"
				>
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					{`+ ${numberOfAdditionalProducts}`}
				</FilterButton>
			)}
		</Box>
	);
};
